import ModelAbstract from './ModelAbstract';

class Company extends ModelAbstract {
    constructor({
        id = null,
        name = '',
        email = '',
        address = '',
        phoneNumber = '',
        description = '',
        currentAvatar = '',
        currentBanner = '',
        type = null,
        status = 'PENDING',
        rejectionReason = null,
        approvedBy = null,
        approvedByRole = null,
        approvedDate = null,
        createdBy = null,
        createdByRole = 'user',
        figures = [],
        createdAt = null,
        updatedAt = null,
        isOwner = false,
        canManage = false,
        isFollowed = false,
        followerCount = 0,
        postCount = 0,
    } = {}) {
        super({ id, createdAt, updatedAt });
        this.name = name;
        this.email = email;
        this.address = address;
        this.phoneNumber = phoneNumber;
        this.description = description;
        this.currentAvatar = currentAvatar;
        this.currentBanner = currentBanner;
        this.type = type;
        this.status = status;
        this.rejectionReason = rejectionReason;
        this.approvedBy = approvedBy;
        this.approvedByRole = approvedByRole;
        this.approvedDate = approvedDate;
        this.createdBy = createdBy;
        this.createdByRole = createdByRole;
        this.figures = figures;
        this.isOwner = isOwner;
        this.canManage = canManage;
        this.isFollowed = isFollowed;
        this.followerCount = followerCount;
        this.postCount = postCount;
    }

    /**
     * Factory method to create a Company instance from API response
     * @param {Object} data - Raw company data from API
     * @returns {Company} - Instantiated Company object
     */
    static fromAPI(data) {
        if (!data) return null;
        
        return new Company({
            id: data.id,
            name: data.name,
            email: data.email,
            address: data.address,
            phoneNumber: data.phoneNumber,
            description: data.description,
            currentAvatar: data.currentAvatar,
            currentBanner: data.currentBanner,
            type: data.type,
            status: data.status,
            rejectionReason: data.rejectionReason,
            approvedBy: data.approvedBy?.email || data.approvedBy,
            approvedByRole: data.approvedByRole || data.approvedBy?.role,
            approvedDate: data.approvedDate,
            createdBy: data.createdBy?.email || data.createdBy,
            createdByRole: data.createdByRole || data.createdBy?.role,
            figures: data.figures || [],
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            isOwner: data.isOwner || false,
            canManage: data.canManage || false,
            isFollowed: data.isFollowed || false,
            followerCount: data.followerCount || 0,
            postCount: data.postCount || 0,
        });
    }

    /**
     * Convert to plain object for JSON serialization
     */
    toJSON() {
        return {
            ...super.toJSON(),
            name: this.name,
            email: this.email,
            address: this.address,
            phoneNumber: this.phoneNumber,
            description: this.description,
            currentAvatar: this.currentAvatar,
            currentBanner: this.currentBanner,
            type: this.type,
            status: this.status,
            rejectionReason: this.rejectionReason,
            approvedBy: this.approvedBy,
            approvedByRole: this.approvedByRole,
            approvedDate: this.approvedDate,
            createdBy: this.createdBy,
            createdByRole: this.createdByRole,
        };
    }

    /**
     * Check if company is approved
     * @returns {boolean}
     */
    isApproved() {
        return this.status === 'APPROVED';
    }

    /**
     * Check if company is pending
     * @returns {boolean}
     */
    isPending() {
        return this.status === 'PENDING';
    }

    /**
     * Check if company is rejected
     * @returns {boolean}
     */
    isRejected() {
        return this.status === 'REJECTED';
    }

    /**
     * Get status display text
     * @returns {string}
     */
    getStatusText() {
        switch (this.status) {
            case 'APPROVED':
                return 'Approved';
            case 'PENDING':
                return 'Pending';
            case 'REJECTED':
                return 'Rejected';
            default:
                return 'Unknown';
        }
    }
}

export default Company;